/**
 * `MarkdownLite` — renderizador minimal de markdown, segura por
 * construcción.
 *
 * **Sprint 18.6.2 (mayo 20, 2026)** — el Asistente IA del expediente
 * devuelve respuestas con sintaxis markdown (`**negrita**`, listas con
 * `- ` y `1. `, párrafos separados por línea en blanco). Antes lo
 * pintábamos como texto plano y el funcionario veía asteriscos
 * literales:
 *
 *     Desde **approved**, el expediente puede transicionar a:
 *     1. **disbursed** — cuando se complete el desembolso.
 *
 * Ahora lo convertimos a HTML controlado: `<strong>`, `<em>`, `<ul>`,
 * `<ol>`, `<p>`.
 *
 * ## Por qué un renderer in-house en vez de `react-markdown`
 *
 * - Bundle: `react-markdown` + transitivas pesan ~80kb gzipped. Para
 *   el subset que necesitamos (4-5 features) no compensa.
 * - **Seguridad por design**: NO parseamos links, imágenes, HTML
 *   inline ni code fences. Cero superficie XSS sin necesidad de
 *   sanitizar — los inputs solo producen los nodos tipados que
 *   abajo se enumeran.
 * - Si el modelo escupe `<script>...</script>` en una respuesta, se
 *   renderiza como texto literal (React escapea por default y
 *   nosotros no usamos `dangerouslySetInnerHTML`).
 *
 * ## Subset soportado
 *
 * - `**texto**` → `<strong>texto</strong>` (acepta también `__texto__`).
 * - `*texto*` → `<em>texto</em>` (acepta también `_texto_`).
 * - Líneas que empiecen con `- ` o `* ` → item de lista no ordenada.
 * - Líneas que empiecen con `1. ` (cualquier dígito) → item de lista ordenada.
 * - Listas consecutivas se agrupan en un solo `<ul>` o `<ol>`.
 * - Línea en blanco entre párrafos → `<p>` separados.
 * - Salto de línea simple dentro del párrafo → preservado con `<br>`.
 *
 * ## NO soportados (intencional)
 *
 * - Headings (`# `): el asistente no debería generarlos en respuestas
 *   cortas.
 * - Links (`[texto](url)`): no esperamos URLs en la respuesta y
 *   evitar `<a>` reduce superficie de phishing.
 * - Code blocks: el asistente no devuelve código.
 * - Tablas: V2 si lo necesitamos.
 */

import type { ReactNode } from "react";

const BOLD_RX = /\*\*([^*]+?)\*\*|__([^_]+?)__/g;
// Italic requiere word-boundary: el marcador NO puede estar entre
// caracteres alfanuméricos (evita matchear `3*5 = 15`) y el contenido
// no puede empezar/terminar en espacio. Soporta `*x*` (single char)
// y `*texto largo*`.
const ITALIC_RX =
  /(?<![*A-Za-z0-9])\*([^*\s](?:[^*]*?[^*\s])?)\*(?![*A-Za-z0-9])|(?<![_A-Za-z0-9])_([^_\s](?:[^_]*?[^_\s])?)_(?![_A-Za-z0-9])/g;
const UNORDERED_ITEM_RX = /^[-*]\s+(.*)$/;
const ORDERED_ITEM_RX = /^\d+\.\s+(.*)$/;

/**
 * Convierte una línea de texto a un array de ReactNode aplicando
 * énfasis inline (bold + italic). Cualquier otro carácter se mantiene
 * como texto plano (React escapea automáticamente).
 */
function renderInline(line: string): ReactNode[] {
  // Procesamos bold primero — los matches de bold consumen los `**`
  // antes de que italic pueda confundirlos con su propio marcador.
  const nodes: ReactNode[] = [];
  let cursor = 0;
  const boldMatches = Array.from(line.matchAll(BOLD_RX));
  for (const match of boldMatches) {
    const start = match.index ?? 0;
    if (start > cursor) {
      nodes.push(...renderInlineNonBold(line.slice(cursor, start)));
    }
    const inner = match[1] ?? match[2] ?? "";
    nodes.push(<strong key={`b-${start}`}>{inner}</strong>);
    cursor = start + match[0].length;
  }
  if (cursor < line.length) {
    nodes.push(...renderInlineNonBold(line.slice(cursor)));
  }
  return nodes;
}

function renderInlineNonBold(segment: string): ReactNode[] {
  // Italic en lo que queda. La regex usa lookbehind / lookahead para
  // requerir word-boundaries (no matchea dentro de `abc*def*ghi` ni
  // dentro de expresiones aritméticas tipo `3*5`).
  const nodes: ReactNode[] = [];
  let cursor = 0;
  const matches = Array.from(segment.matchAll(ITALIC_RX));
  for (const match of matches) {
    const start = match.index ?? 0;
    const inner = match[1] ?? match[2] ?? "";
    if (start > cursor) {
      nodes.push(segment.slice(cursor, start));
    }
    nodes.push(<em key={`i-${start}`}>{inner}</em>);
    cursor = start + match[0].length;
  }
  if (cursor < segment.length) {
    nodes.push(segment.slice(cursor));
  }
  return nodes;
}

type Block =
  | { type: "paragraph"; lines: string[] }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] };

/**
 * Parsea el texto crudo en bloques. Líneas en blanco separan
 * paragrafos; líneas con `- ` / `* ` se agrupan en `ul`; líneas con
 * `1. ` se agrupan en `ol`. Bloques consecutivos del mismo tipo se
 * mergen.
 */
function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  // Normalizamos \r\n → \n para no romper en Windows.
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  let current: Block | null = null;

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === "") {
      if (current) {
        blocks.push(current);
        current = null;
      }
      continue;
    }
    const ulMatch = line.match(UNORDERED_ITEM_RX);
    const olMatch = line.match(ORDERED_ITEM_RX);
    if (ulMatch) {
      // El grupo 1 es el contenido capturado por `^[-*]\s+(.*)$`. La
      // regex garantiza match → grupo 1 existe; defensivo con ?? "".
      const itemText = ulMatch[1] ?? "";
      if (current && current.type === "ul") {
        current.items.push(itemText);
      } else {
        if (current) blocks.push(current);
        current = { type: "ul", items: [itemText] };
      }
      continue;
    }
    if (olMatch) {
      const itemText = olMatch[1] ?? "";
      if (current && current.type === "ol") {
        current.items.push(itemText);
      } else {
        if (current) blocks.push(current);
        current = { type: "ol", items: [itemText] };
      }
      continue;
    }
    // Línea normal — extiende el párrafo activo o crea uno nuevo.
    if (current && current.type === "paragraph") {
      current.lines.push(line);
    } else {
      if (current) blocks.push(current);
      current = { type: "paragraph", lines: [line] };
    }
  }
  if (current) blocks.push(current);
  return blocks;
}

export function MarkdownLite({ source }: { source: string }) {
  const blocks = parseBlocks(source);
  return (
    <div data-testid="markdown-lite" className="space-y-2">
      {blocks.map((block, idx) => {
        if (block.type === "ul") {
          return (
            <ul key={idx} className="ml-5 list-disc space-y-1">
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={idx} className="ml-5 list-decimal space-y-1">
              {block.items.map((item, j) => (
                <li key={j}>{renderInline(item)}</li>
              ))}
            </ol>
          );
        }
        // paragraph — líneas internas con <br/>
        return (
          <p key={idx} className="whitespace-pre-wrap">
            {block.lines.map((line, j) => (
              <span key={j}>
                {renderInline(line)}
                {j < block.lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
